import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface OutputFile {
  id: string;
  filename: string;
  adapter: string;
  createdAt: string;
  size: string;
  type: 'json' | 'csv';
  fullPath: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

export const useRecentOutputs = (limit: number = 5) => {
  const [files, setFiles] = useState<OutputFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadOutputs = useCallback(async () => {
    try {
      // List all folders (adapters) in the scraped-data bucket
      const { data: folders, error: foldersError } = await supabase.storage
        .from('scraped-data')
        .list('', { limit: 100 });

      if (foldersError) {
        console.error('Error listing folders:', foldersError);
        return;
      }

      const allFiles: OutputFile[] = [];

      // For each adapter folder, list files
      for (const folder of folders || []) {
        if (folder.id === null) {
          // It's a folder (adapter directory)
          const { data: adapterFiles, error: filesError } = await supabase.storage
            .from('scraped-data')
            .list(folder.name, { 
              limit: 20,
              sortBy: { column: 'created_at', order: 'desc' }
            });

          if (filesError) {
            console.error(`Error listing files in ${folder.name}:`, filesError);
            continue;
          }

          for (const file of adapterFiles || []) {
            if (file.id !== null) {
              const fileExtension = file.name.split('.').pop()?.toLowerCase();
              allFiles.push({
                id: file.id,
                filename: file.name,
                adapter: folder.name,
                createdAt: file.created_at || new Date().toISOString(),
                size: formatFileSize(file.metadata?.size || 0),
                type: fileExtension === 'csv' ? 'csv' : 'json',
                fullPath: `${folder.name}/${file.name}`,
              });
            }
          }
        } else {
          // It's a file at root level
          const fileExtension = folder.name.split('.').pop()?.toLowerCase();
          allFiles.push({
            id: folder.id,
            filename: folder.name,
            adapter: 'unknown',
            createdAt: folder.created_at || new Date().toISOString(),
            size: formatFileSize(folder.metadata?.size || 0),
            type: fileExtension === 'csv' ? 'csv' : 'json',
            fullPath: folder.name,
          });
        }
      }

      // Sort by created_at descending and take the limit
      allFiles.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Resolve adapters for root-level files using job_runs output paths
      const unknownPaths = allFiles
        .filter((file) => file.adapter === 'unknown')
        .map((file) => file.fullPath);

      if (unknownPaths.length > 0) {
        const { data: runs } = await supabase
          .from('job_runs')
          .select('output_file_path, job_id, scrape_jobs(adapter_id, adapters(slug, name))')
          .in('output_file_path', unknownPaths);

        const outputToAdapter = new Map<string, string>();
        const jobIdsNeedingAdapter: string[] = [];

        for (const run of runs || []) {
          const outputPath = run.output_file_path as string | null;
          const adapterSlug = (run.scrape_jobs as { adapters?: { slug?: string } | null })?.adapters?.slug;
          if (outputPath && adapterSlug) {
            outputToAdapter.set(outputPath, adapterSlug);
          } else if (run.job_id) {
            jobIdsNeedingAdapter.push(run.job_id as string);
          }
        }

        if (jobIdsNeedingAdapter.length > 0) {
          const { data: jobAdapters } = await supabase
            .from('job_adapters')
            .select('job_id, adapters(slug)')
            .in('job_id', jobIdsNeedingAdapter);

          const jobIdToAdapter = new Map<string, string>();
          for (const row of jobAdapters || []) {
            const slug = (row.adapters as { slug?: string } | null)?.slug;
            if (row.job_id && slug && !jobIdToAdapter.has(row.job_id)) {
              jobIdToAdapter.set(row.job_id, slug);
            }
          }

          for (const run of runs || []) {
            const outputPath = run.output_file_path as string | null;
            const adapterSlug = jobIdToAdapter.get(run.job_id as string);
            if (outputPath && adapterSlug && !outputToAdapter.has(outputPath)) {
              outputToAdapter.set(outputPath, adapterSlug);
            }
          }
        }

        if (outputToAdapter.size > 0) {
          for (const file of allFiles) {
            const resolved = outputToAdapter.get(file.fullPath);
            if (resolved) file.adapter = resolved;
          }
        }
      }

      // Drop root-level files that never resolved to a real adapter slug. These
      // are almost always test artefacts (e.g. `*-adapter-test-snapshot-*.json`)
      // and surfacing them as "unknown" in Recent Outputs is misleading.
      const resolvedFiles = allFiles.filter((file) => file.adapter !== 'unknown');
      setFiles(resolvedFiles.slice(0, limit));
    } catch (error) {
      console.error('Error loading recent outputs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  const downloadFile = async (file: OutputFile) => {
    try {
      const { data, error } = await supabase.storage
        .from('scraped-data')
        .download(file.fullPath);

      if (error) {
        console.error('Error downloading file:', error);
        return;
      }

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  useEffect(() => {
    loadOutputs();

    // Subscribe to job_runs completion to refresh file list
    const channel = supabase
      .channel('recent_outputs')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'job_runs',
          filter: 'status=eq.completed',
        },
        () => {
          // Delay slightly to allow storage upload to complete
          setTimeout(loadOutputs, 2000);
        }
      )
      .subscribe((status) => {
        console.log('Recent outputs subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadOutputs]);

  return { files, isLoading, refetch: loadOutputs, downloadFile };
};
